import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, IntlShape } from 'react-intl';
import OECheckbox from '../../../../../global/oe-checkbox/oe-checkbox';
import { ArtifactsTypeForBatchRequest, RecordingType, ResolutionMp4 } from '../../../../../../api/artifacts.dao';
import { artifactMp4RecordingTypes, artifactMp4Values, artifactTypeLabel } from '../../../../../../constants/artifacts.constants';
import classnames from 'classnames';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import { KnovioLabel } from '../../../../../../api/connections.dao';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';

export interface IRequestArtifactsSettingsProps {
    intl: IntlShape;
    selectedArtifactTypes: Set<ArtifactsTypeForBatchRequest>;
    handleArtifactTypeCheckBox: (type: ArtifactsTypeForBatchRequest) => void;
    handleResolutionDropdown: (type: ResolutionMp4) => void;
    handleRecordingTypeDropdown: (type: RecordingType) => void;
    resolutionType: ResolutionMp4;
    recordingType: RecordingType;
    isKnovioConnectionExist: boolean;
    isWebinarConnectionExist: boolean;
}

const RequestArtifactsSettings = observer(({
    intl,
    selectedArtifactTypes,
    handleArtifactTypeCheckBox,
    handleResolutionDropdown,
    resolutionType,
    isKnovioConnectionExist,
    handleRecordingTypeDropdown,
    recordingType,
    isWebinarConnectionExist,
}: IRequestArtifactsSettingsProps) => {

    // Users with only 'Request AI Transcript' can request Transcript, but not MP3 or MP4 (OEP-30539).
    const availableArtifactTypes: ArtifactsTypeForBatchRequest[] = hasPermission(Permission.REQUEST_ARTIFACTS)
        ? Object.values(ArtifactsTypeForBatchRequest)
        : [ArtifactsTypeForBatchRequest.TRANSCRIPT];

    return (
        <section>
            <div className='flex'>
                <div>
                    <h3 className='request-artifacts-popover-types-title'>
                        {
                            intl.formatMessage({
                                description: 'Text',
                                defaultMessage: 'Artifact Types',
                                id: 'oe.artifact.artifactTypes'
                            })
                        }
                    </h3>
                    <div className='request-artifacts-popover-types flex'>
                        {
                            availableArtifactTypes
                                .map(type => {
                                    return <OECheckbox
                                        key={type}
                                        checked={selectedArtifactTypes.has(type)}
                                        onChange={() => handleArtifactTypeCheckBox(type)}
                                        label={artifactTypeLabel[type]}
                                        classNames='request-artifacts-popover-type m-right-50px'
                                    />;
                                })
                        }
                    </div>
                </div>
                {<div className={classnames('flex', { 'request-artifacts-mp4-options__hidden': !selectedArtifactTypes.has(ArtifactsTypeForBatchRequest.MP4) })}>
                    <div>
                        <div className='request-artifacts-mp4-options'>
                            <h3 className='request-artifacts-popover-types-title m-bottom-5px'>
                                <FormattedMessage
                                    id='oe.artifact.mp4ResolutionKnovio'
                                    defaultMessage={`MP4 Resolution (${KnovioLabel.OEStream})`}
                                    description='Label text'
                                />

                            </h3>
                            <InfoPopover>
                                <FormattedMessage
                                    id='oe.artifact.infoKnovioResolution'
                                    defaultMessage={`Choice of resolution is only available for ${KnovioLabel.OEStream} meetings.
                                            If any selected meeting does not have a ${KnovioLabel.OEStream}, no Resolution setting will be applied;
                                            you will receive the default layout setting from Zoom`}
                                    description='Label text'
                                />
                            </InfoPopover>
                        </div>
                        <select
                            value={resolutionType}
                            onChange={e => handleResolutionDropdown(e.target.value as ResolutionMp4)}
                            className='oe-form-field__control'
                            disabled={!isKnovioConnectionExist}
                        >
                            {artifactMp4Values(intl).map(({ value, label }, i) => <option value={value} key={i}>{label}</option>)}
                        </select>
                    </div>
                    <div className='m-left-10px'>
                        <div className='request-artifacts-mp4-options'>
                            <h3 className='request-artifacts-popover-types-title m-bottom-5px'>
                                <FormattedMessage
                                    id='oe.artifact.mp4RecordingTypesWebinar'
                                    defaultMessage='MP4 Format (Webinar)'
                                    description='Label text'
                                />

                            </h3>
                            <InfoPopover>
                                <FormattedMessage
                                    id='oe.artifact.infoKnovioResolution'
                                    defaultMessage={`Choice of format is only available for Webinar meetings.
                                        If any selected meeting has a ${KnovioLabel.OEStream}, no format setting will be applied;
                                        you will receive the MP4 from ${KnovioLabel.OEStream}.
                                    `}
                                    description='Label text'
                                />
                            </InfoPopover>
                        </div>
                        <select
                            value={recordingType}
                            onChange={e => handleRecordingTypeDropdown(e.target.value as RecordingType)}
                            className='oe-form-field__control'
                            disabled={!isWebinarConnectionExist}
                        >
                            {artifactMp4RecordingTypes(intl).map(({ value, label }) => <option value={value} key={value}>{label}</option>)}
                        </select>

                    </div>
                </div>
                }
            </div>
        </section>
    );

});

export default RequestArtifactsSettings;